// packages/web/src/process/useStepEdgeStyleResolver.ts
//
// Resolves a per-step edge style by looking up the callee interface in
// the model and aggregating its widget config. Shared by Flow,
// Sequence, and Block-scheme renderers so all three respect the same
// interface-driven styling (dashed for events, custom color, etc.).

import { useMemo } from "react";
import type { WalkResult } from "@archlang/engine";
import type { ArchitectureModel, Interface, Module, Surface } from "@archlang/scene/arch-model";
import { aggregateInterfaceStyle, type EdgeStyle } from "@archlang/scene/arch-model";
import type { StepEdgeStyle } from "./SwimlaneView.js";

export function useStepEdgeStyleResolver(
  model: ArchitectureModel | null,
  walkResult: WalkResult | null,
): ((stepIndex: number) => StepEdgeStyle | undefined) | undefined {
  // Build qualifiedName → Interface map once per model.
  const interfaceByPath = useMemo(() => {
    const out = new Map<string, Interface>();
    if (!model) return out;
    walkInterfaces(model.modules, [], out);
    return out;
  }, [model]);

  return useMemo(() => {
    if (!walkResult) return undefined;
    return (stepIndex: number): StepEdgeStyle | undefined => {
      const step = walkResult.byIndex.get(stepIndex);
      if (!step) return undefined;
      if (step.noteText !== undefined) return undefined;
      // Saga compensation (unwind) + retry come from the step's modifiers and
      // apply regardless of whether the callee interface carries widget styling
      // — so the rollback / retry path is always marked.
      const compensation = step.modifiers?.compensation === true;
      const dist = step.modifiers?.dist === true;
      const iface = interfaceByPath.get(step.to.join("."));
      const style: EdgeStyle | undefined = iface ? aggregateInterfaceStyle([iface]) : undefined;
      if (!style && !compensation && !dist) return undefined;
      return {
        color: style?.color,
        // Compensation nodes render SOLID orange (handled in the block), not
        // dashed — only the per-step error (drop) arrows are dashed.
        dashed: style?.dashed,
        width: style?.width,
        opacity: style?.opacity,
        ...(compensation ? { compensation: true } : {}),
        ...(dist ? { dist: true } : {}),
      };
    };
  }, [walkResult, interfaceByPath]);
}

function walkInterfaces(
  modules: readonly Module[] | readonly Surface[],
  parentPath: readonly string[],
  out: Map<string, Interface>,
): void {
  for (const m of modules) {
    const path = [...parentPath, m.name];
    for (const i of m.interfaces) {
      out.set([...path, i.name].join("."), i);
    }
    if (m.surfaces.length > 0) {
      walkInterfaces(m.surfaces, path, out);
    }
    if ("children" in m && m.children.length > 0) {
      walkInterfaces(m.children, path, out);
    }
  }
}
